/**
 * 担当の欄（#100、docs/39 §3.3、Figma A2 の7）。1つの欄で打つと候補：
 *   業者（外注先・両方）／自社の人／この案件の{customer} → 合わないときは「「ガス会社」と書く」「「ガス会社」を業者に登録」→ いちばん下に「担当なし」
 *   ↓↑ で選ぶ・Enter で決める（選んでいなければ打った文字で「書く」）・Esc でやめる。欄を出るときに打ち変えていれば「書く」（空なら担当なし）
 *   候補の枠は画面に対して置く（表の横に流れる枠で切れない）
 * #102 §5.1：業者・{customer}は略称（大きく）と正式名（違えば小さく）。探すのは 略称・正式名・フリガナ。欄を指すと title で正式名。
 *   空の欄の「担当を選ぶ」は、行を指したとき・選んだとき・欄に入ったときだけ（CSS。ふだんは空＝「担当」という名の業者に見えない）
 */
import { type ScheduleAssignee, type ScheduleAssignees } from "@genbaryoku/contract";
import { SCHEDULE_MSG } from "./msg.js";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLabels } from "../../labels.js";

type Opt = { key: string; label: string; full?: string | undefined; sub?: string; pick: () => void | Promise<void>; kind: "cand" | "write" | "register" | "none" };

export function assigneeLabel(a: ScheduleAssignee | null, cands?: ScheduleAssignees): string {
  if (!a) return "";
  if (a.type === "text") return a.text;
  if (a.name) return a.name;
  const list = a.type === "partner" ? [...(cands?.vendors ?? []), ...(cands?.clients ?? [])] : (cands?.users ?? []);
  return list.find((x) => x.id === a.id)?.name ?? "";
}
/** 欄を指したときの title：業者・{customer}は正式名（候補にあれば）、ほかは表示のまま */
export function assigneeTitle(a: ScheduleAssignee | null, cands?: ScheduleAssignees): string {
  if (!a) return "";
  if (a.type === "partner") {
    const p = [...(cands?.vendors ?? []), ...(cands?.clients ?? [])].find((x) => x.id === a.id);
    if (p?.fullName) return p.fullName;
  }
  return assigneeLabel(a, cands);
}

export function AssigneeInput({
  value,
  cands,
  onChange,
  onRegister,
  disabled,
  label,
}: {
  value: ScheduleAssignee | null;
  cands: ScheduleAssignees | undefined;
  onChange: (a: ScheduleAssignee | null) => void;
  /** 業者に登録して担当にする（POST /partners）。登録できた業者の id を返す */
  onRegister: (name: string) => Promise<string | null>;
  disabled?: boolean;
  label: string;
}) {
  const L = useLabels();
  const shown = assigneeLabel(value, cands);
  const [text, setText] = useState(shown);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const id = useId();
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(shown);
  }, [shown]);

  const q = text.trim();
  const has = (...xs: (string | undefined)[]) => !q || xs.some((s) => !!s && s.toLowerCase().includes(q.toLowerCase()));
  const exact = [...(cands?.vendors ?? []), ...(cands?.clients ?? [])].some((x) => x.name === q || x.fullName === q) || (cands?.users ?? []).some((x) => x.name === q);
  const fullOf = (p: { name: string; fullName: string }) => (p.fullName && p.fullName !== p.name ? p.fullName : undefined);
  const close = () => {
    setOpen(false);
    setHi(-1);
  };
  const choose = (a: ScheduleAssignee | null) => {
    close();
    onChange(a);
    setText(a ? (a.type === "text" ? a.text : (a.name ?? "")) : "");
  };
  const opts: Opt[] = [
    ...(cands?.vendors ?? [])
      .filter((v) => has(v.name, v.fullName, v.kana))
      .slice(0, 8)
      .map((v): Opt => ({ key: `p${v.id}`, label: v.name, full: fullOf(v), sub: L.vendor, kind: "cand", pick: () => choose({ type: "partner", id: v.id, name: v.name }) })),
    ...(cands?.users ?? []).filter((u) => has(u.name)).slice(0, 8).map((u): Opt => ({ key: `u${u.id}`, label: u.name, sub: "自社", kind: "cand", pick: () => choose({ type: "user", id: u.id, name: u.name }) })),
    ...(cands?.clients ?? [])
      .filter((c) => has(c.name, c.fullName, c.kana))
      .map((c): Opt => ({ key: `c${c.id}`, label: c.name, full: fullOf(c), sub: L.customer, kind: "cand", pick: () => choose({ type: "partner", id: c.id, name: c.name }) })),
    ...(q && !exact
      ? [
          { key: "write", label: SCHEDULE_MSG.writeAs(q), kind: "write", pick: () => choose({ type: "text", text: q.slice(0, 60) }) } satisfies Opt,
          {
            key: "register",
            label: SCHEDULE_MSG.registerVendor(q),
            kind: "register",
            pick: async () => {
              close();
              const pid = await onRegister(q);
              if (pid) choose({ type: "partner", id: pid, name: q });
            },
          } satisfies Opt,
        ]
      : []),
    { key: "none", label: SCHEDULE_MSG.noAssignee, kind: "none", pick: () => choose(null) },
  ];
  const noCand = !opts.some((o) => o.kind === "cand");

  const place = () => {
    const r = input.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(280, r.width);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    setPos({ top: r.bottom + 4, left, width });
  };
  useLayoutEffect(() => {
    if (open) place();
  }, [open, text]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (input.current?.contains(e.target as Node) || pop.current?.contains(e.target as Node)) return;
      close();
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  /** 欄を出るとき：打ち変えていれば「書く」（空なら担当なし） */
  const commitTyped = () => {
    if (text === shown) return;
    if (!q) onChange(null);
    else {
      const hit = [
        ...(cands?.vendors ?? []).map((v) => ({ id: v.id, name: v.name, fullName: v.fullName, t: "partner" as const })),
        ...(cands?.users ?? []).map((u) => ({ id: u.id, name: u.name, fullName: "", t: "user" as const })),
      ].find((x) => x.name === q || (!!x.fullName && x.fullName === q));
      onChange(hit ? { type: hit.t, id: hit.id, name: hit.name } : { type: "text", text: q.slice(0, 60) });
    }
  };

  const title = assigneeTitle(value, cands);
  if (disabled) return <span className="sch-asg ro" title={title || undefined}>{shown}</span>;
  return (
    <>
      <input
        ref={input}
        className="sch-in sch-asg"
        value={text}
        title={title || undefined}
        placeholder={SCHEDULE_MSG.assigneePick}
        aria-label={label}
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        maxLength={60}
        onFocus={() => {
          focused.current = true;
          setOpen(true);
        }}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setHi(-1);
        }}
        onBlur={() => {
          focused.current = false;
          // 候補を押している最中（mousedown → blur）は、押した候補に任せる
          setTimeout(() => {
            if (pop.current?.contains(document.activeElement)) return;
            if (open) close();
            commitTyped();
          }, 0);
        }}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing || e.keyCode === 229) return;
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            setHi((h) => (e.key === "ArrowDown" ? (h + 1) % opts.length : (h - 1 + opts.length) % opts.length));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const o = hi >= 0 ? opts[hi] : undefined;
            if (o) void o.pick();
            else {
              close();
              commitTyped();
            }
          } else if (e.key === "Escape") {
            if (open) {
              e.preventDefault();
              e.stopPropagation();
              close();
              setText(shown);
            }
          }
        }}
      />
      {open && pos
        ? createPortal(
            <div ref={pop} className="sch-asgpop" id={id} role="listbox" aria-label={label} style={{ top: pos.top, left: pos.left, width: pos.width }}>
              {noCand && q ? <div className="sch-asgnone">{SCHEDULE_MSG.noMatch}</div> : null}
              {opts.map((o, i) => (
                <button
                  key={o.key}
                  type="button"
                  role="option"
                  aria-selected={i === hi}
                  className={`sch-asgopt k-${o.kind}${i === hi ? " hi" : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void o.pick()}
                  data-testid={`asg-opt-${o.kind}`}
                >
                  <span className="nm">
                    <span>{o.label}</span>
                    {o.full ? <small className="full">{o.full}</small> : null}
                  </span>
                  {o.sub ? <small>{o.sub}</small> : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
